import { z } from 'zod';
import {
  DUNNING_GRACE_DAYS,
  PLANS_IN_ORDER,
  cheapestPlanWithModule,
  getPlan,
  planKeySchema,
  type PlanDefinition,
  type PlanKey,
  type PlanModule,
} from '../constants/plans.js';
// Type-only, and therefore erased: `quotas.ts` imports `PlanRefusal` from here, so a value
// import in this direction would be a runtime cycle. What it buys is that `limit` names the
// exact meter that refused rather than a generic "quota", which is what lets the client write
// a different sentence for a customer book than for a photo library.
import type { MeteredResource } from './quotas.js';

/**
 * THE ENTITLEMENT SERVICE — the one place that answers "may this shop do that?".
 *
 * Both sides read it. The API refuses with it, the browser hides with it, and because they
 * call the same functions over the same snapshot they cannot disagree: a hidden button in
 * front of an open endpoint is a security bug, and a visible button in front of a closed one
 * is a support ticket. Reimplementing the rules in the UI would produce both, eventually.
 *
 * Everything here is a pure function of a {@link SubscriptionSnapshot} and the clock. No
 * database, no request, no React — which is what lets one implementation serve both runtimes.
 *
 * Two axes, and they are genuinely different questions:
 *
 *  - **the plan** decides which MODULES a shop has bought;
 *  - **the subscription** decides whether the shop may write at all today.
 *
 * Both answers are `full` or `read_only`, never "gone". Losing an entitlement must never
 * delete or hide the data behind it: the module goes read-only, the rows stay exactly where
 * they were, and re-upgrading restores writing with the history intact. A shop that lapses
 * for a fortnight and comes back must find its order book, not a fresh install.
 */

/** Where a tenant stands commercially. Task 24 owns the lifecycle that moves it. */
export const SUBSCRIPTION_STATUSES = [
  'trialing',
  'active',
  'past_due',
  'read_only',
  'cancelled',
] as const;
export type SubscriptionStatus = (typeof SUBSCRIPTION_STATUSES)[number];
export const subscriptionStatusSchema = z.enum(SUBSCRIPTION_STATUSES);

/**
 * The commercial facts every entitlement decision is made from.
 *
 * Dates are accepted as `Date` or as the ISO strings they become crossing the wire, so the
 * server can pass a Mongo document and the browser can pass a parsed response body without
 * either of them converting first.
 */
export interface SubscriptionSnapshot {
  readonly planKey: PlanKey;
  readonly status: SubscriptionStatus;
  readonly trialEndsAt?: Date | string | null;
  readonly currentPeriodEndsAt?: Date | string | null;
}

/** What a shop may do with a module right now. Never "none" — see the note above. */
export type AccessMode = 'full' | 'read_only';

/** Why access is what it is. `entitled` is the happy path; the rest name the refusal. */
export type AccessReason = 'entitled' | 'module_not_in_plan' | 'subscription_read_only';

export interface ModuleAccess {
  readonly module: PlanModule;
  readonly mode: AccessMode;
  readonly reason: AccessReason;
  /** The cheapest plan that would restore writing, when a plan is what is missing. */
  readonly requiredPlan: PlanKey | null;
}

const DAY_MS = 86_400_000;

function toDate(value: Date | string | null | undefined): Date | null {
  if (value === null || value === undefined) return null;
  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

/** Whole days from `now` until `at`, never negative. `null` when there is no such date. */
function daysUntil(at: Date | null, now: Date): number | null {
  if (!at) return null;
  return Math.max(0, Math.ceil((at.getTime() - now.getTime()) / DAY_MS));
}

// --- The subscription axis ---------------------------------------------------------------------

/**
 * Whether the shop may write at all today, ignoring which modules it has bought.
 *
 * A missing date means "no clock has been set", and an absent clock must never lock a shop
 * out: a trial with no end date, or a lapsed subscription with no period end recorded, reads
 * as full access rather than as an expiry that happened at the epoch.
 */
export function subscriptionAccess(
  snapshot: SubscriptionSnapshot,
  now: Date = new Date(),
): AccessMode {
  switch (snapshot.status) {
    case 'active':
      return 'full';
    case 'trialing': {
      const endsAt = toDate(snapshot.trialEndsAt);
      return endsAt && endsAt.getTime() <= now.getTime() ? 'read_only' : 'full';
    }
    case 'past_due': {
      // The grace period is the whole point of dunning: locking a shop out of its live order
      // book the morning a card expires makes you the villain in a story about their bank.
      const endsAt = toDate(snapshot.currentPeriodEndsAt);
      if (!endsAt) return 'full';
      return now.getTime() <= endsAt.getTime() + DUNNING_GRACE_DAYS * DAY_MS
        ? 'full'
        : 'read_only';
    }
    case 'read_only':
    case 'cancelled':
      return 'read_only';
  }
}

/** Days of trial left, or `null` when the shop is not on one. Zero on the last day. */
export function trialDaysRemaining(
  snapshot: SubscriptionSnapshot,
  now: Date = new Date(),
): number | null {
  if (snapshot.status !== 'trialing') return null;
  return daysUntil(toDate(snapshot.trialEndsAt), now);
}

/** When the shop next renews (or its trial ends) — the date the billing screen shows. */
export function renewalDate(snapshot: SubscriptionSnapshot): Date | null {
  return snapshot.status === 'trialing'
    ? toDate(snapshot.trialEndsAt)
    : toDate(snapshot.currentPeriodEndsAt);
}

/** Days of dunning grace left before a past-due shop goes read-only. */
export function graceDaysRemaining(
  snapshot: SubscriptionSnapshot,
  now: Date = new Date(),
): number | null {
  if (snapshot.status !== 'past_due') return null;
  const endsAt = toDate(snapshot.currentPeriodEndsAt);
  if (!endsAt) return null;
  return daysUntil(new Date(endsAt.getTime() + DUNNING_GRACE_DAYS * DAY_MS), now);
}

// --- The plan axis ------------------------------------------------------------------------------

/**
 * What the shop may do with one module: the stricter of the two axes, with the reason.
 *
 * The order matters. A module the shop never bought reports `module_not_in_plan` even when
 * the subscription is also read-only, because that is the refusal the owner can act on —
 * "your plan does not include this" names a purchase; "your subscription is read-only" names
 * a payment they may have already made.
 */
export function moduleAccess(
  snapshot: SubscriptionSnapshot,
  module: PlanModule,
  now: Date = new Date(),
): ModuleAccess {
  const plan = getPlan(snapshot.planKey);
  if (!plan.modules.includes(module)) {
    return {
      module,
      mode: 'read_only',
      reason: 'module_not_in_plan',
      requiredPlan: cheapestPlanWithModule(module)?.key ?? null,
    };
  }
  if (subscriptionAccess(snapshot, now) === 'read_only') {
    return { module, mode: 'read_only', reason: 'subscription_read_only', requiredPlan: null };
  }
  return { module, mode: 'full', reason: 'entitled', requiredPlan: null };
}

/** True when the shop may CHANGE data in `module`. Reading is never refused. */
export function canWrite(
  snapshot: SubscriptionSnapshot,
  module: PlanModule,
  now: Date = new Date(),
): boolean {
  return moduleAccess(snapshot, module, now).mode === 'full';
}

/**
 * True when the module is in the plan at all — the question the NAVIGATION asks.
 *
 * Distinct from {@link canWrite} on purpose: a shop whose card has failed still sees its
 * expense book in the sidebar, because the data is theirs and hiding it would look like loss.
 * A shop that never bought inventory has nothing behind that link at all.
 */
export function hasModule(snapshot: SubscriptionSnapshot, module: PlanModule): boolean {
  return getPlan(snapshot.planKey).modules.includes(module);
}

// --- Seats ---------------------------------------------------------------------------------------

export interface SeatUsage {
  /** Active people who can sign in today, including the owner. */
  readonly used: number;
  /** Invitations sent and not yet accepted. They are seats already spent. */
  readonly pending: number;
  /** `null` means unlimited. */
  readonly allowance: number | null;
  /** Seats left before the next invite is refused. `null` when unlimited. */
  readonly remaining: number | null;
  /** True when one more person cannot be added. */
  readonly atLimit: boolean;
  /** True when the shop is ALREADY over — only reachable by a plan change. */
  readonly exceeded: boolean;
}

/**
 * Seats used against seats bought.
 *
 * Pending invitations count. A shop on three seats that has invited two people has spent
 * them: letting a third invitation through and refusing the person at the moment they try to
 * join would put the refusal in front of the one participant who cannot act on it.
 */
export function seatUsage(
  planKey: PlanKey,
  activeStaff: number,
  pendingInvites = 0,
): SeatUsage {
  const allowance = getPlan(planKey).staffSeats;
  const taken = activeStaff + pendingInvites;
  if (allowance === null) {
    return {
      used: activeStaff,
      pending: pendingInvites,
      allowance: null,
      remaining: null,
      atLimit: false,
      exceeded: false,
    };
  }
  return {
    used: activeStaff,
    pending: pendingInvites,
    allowance,
    remaining: Math.max(0, allowance - taken),
    atLimit: taken >= allowance,
    exceeded: taken > allowance,
  };
}

// --- Refusals the client can act on ---------------------------------------------------------------

/**
 * The structured body every plan refusal carries.
 *
 * A generic "upgrade required" wastes the single moment where someone is most willing to pay.
 * This names the limit, what they are on, what would fix it and what that costs — enough for
 * the client to render a specific, non-punitive prompt without pattern-matching on prose.
 */
export interface PlanRefusal {
  /** Matches `API_ERROR_CODES`, so the envelope's `code` and this agree. */
  readonly code: 'plan_limit_reached' | 'module_not_in_plan' | 'subscription_read_only';
  readonly limit: 'staff_seats' | 'module' | 'subscription' | MeteredResource;
  readonly currentPlan: PlanKey;
  readonly requiredPlan: PlanKey | null;
  readonly requiredPlanLabel: string | null;
  readonly requiredPlanMonthlyPricePaise: number | null;
  readonly module?: PlanModule;
  readonly used?: number;
  readonly allowance?: number;
  readonly subscriptionStatus?: SubscriptionStatus;
}

function requiredPlanFields(key: PlanKey | null): Pick<
  PlanRefusal,
  'requiredPlan' | 'requiredPlanLabel' | 'requiredPlanMonthlyPricePaise'
> {
  if (!key) {
    return { requiredPlan: null, requiredPlanLabel: null, requiredPlanMonthlyPricePaise: null };
  }
  const plan = getPlan(key);
  return {
    requiredPlan: key,
    requiredPlanLabel: plan.defaultLabel,
    requiredPlanMonthlyPricePaise: plan.monthlyPricePaise,
  };
}

/** "Your plan does not include stock." */
export function moduleRefusal(
  snapshot: SubscriptionSnapshot,
  module: PlanModule,
): PlanRefusal {
  return {
    code: 'module_not_in_plan',
    limit: 'module',
    currentPlan: snapshot.planKey,
    module,
    ...requiredPlanFields(cheapestPlanWithModule(module)?.key ?? null),
  };
}

/** "Starter includes 3 people and you have 3." */
export function seatRefusal(snapshot: SubscriptionSnapshot, usage: SeatUsage): PlanRefusal {
  return {
    code: 'plan_limit_reached',
    limit: 'staff_seats',
    currentPlan: snapshot.planKey,
    used: usage.used + usage.pending,
    ...(usage.allowance === null ? {} : { allowance: usage.allowance }),
    ...requiredPlanFields(nextPlanWithSeats(snapshot.planKey, usage.used + usage.pending + 1)),
  };
}

/** "Your subscription is read-only." Nothing to upgrade to — this is a payment, not a tier. */
export function subscriptionRefusal(snapshot: SubscriptionSnapshot): PlanRefusal {
  return {
    code: 'subscription_read_only',
    limit: 'subscription',
    currentPlan: snapshot.planKey,
    subscriptionStatus: snapshot.status,
    ...requiredPlanFields(null),
  };
}

/** The refusal for a write that was denied, or `null` when it should be allowed. */
export function writeRefusal(
  snapshot: SubscriptionSnapshot,
  module: PlanModule,
  now: Date = new Date(),
): PlanRefusal | null {
  const access = moduleAccess(snapshot, module, now);
  if (access.mode === 'full') return null;
  return access.reason === 'module_not_in_plan'
    ? moduleRefusal(snapshot, module)
    : subscriptionRefusal(snapshot);
}

/** The cheapest plan (at or above the current one) that seats `headcount` people. */
export function nextPlanWithSeats(current: PlanKey, headcount: number): PlanKey | null {
  const from = getPlan(current).sortOrder;
  const candidate = PLANS_IN_ORDER.find(
    (plan) =>
      plan.sortOrder > from && (plan.staffSeats === null || plan.staffSeats >= headcount),
  );
  return candidate?.key ?? null;
}

// --- Changing plan ---------------------------------------------------------------------------------

export interface PlanChangeBlocker {
  /**
   * `staff_over_allowance` — people who can sign in today do not fit, so somebody must be
   * deactivated first. `invites_over_allowance` — they fit, but the invitations already sent
   * would not, so an invitation must be revoked. Two reasons because they need two different
   * sentences: one asks the owner to let somebody go, the other only to un-send an email.
   */
  readonly reason: 'staff_over_allowance' | 'invites_over_allowance';
  /** What the shop has now — active people, or active people plus open invitations. */
  readonly current: number;
  /** What the target plan allows. */
  readonly allowance: number;
  /** How many must go before the change may happen. */
  readonly excess: number;
}

export interface PlanChangeAssessment {
  readonly from: PlanKey;
  readonly to: PlanKey;
  readonly direction: 'upgrade' | 'downgrade' | 'same';
  readonly allowed: boolean;
  readonly blockers: readonly PlanChangeBlocker[];
  /** Modules that go read-only on this change. Their data is kept, always. */
  readonly modulesLost: readonly PlanModule[];
  readonly modulesGained: readonly PlanModule[];
}

/**
 * What moving to `to` would do, and whether it may happen at all.
 *
 * Downgrading below the current headcount is BLOCKED rather than applied. The alternative —
 * silently deactivating whoever the query happened to return last — would lock a karigar out
 * mid-shift on a decision the owner never made, and they would ring the shop, not us.
 *
 * Losing a module is not a blocker. It costs nothing to reverse, it destroys nothing, and
 * making an owner dismantle their inventory before they may pay less is how a downgrade
 * becomes a cancellation.
 */
export function assessPlanChange(
  from: PlanKey,
  to: PlanKey,
  usage: SeatUsage,
): PlanChangeAssessment {
  const target = getPlan(to);
  const current = getPlan(from);
  const blockers: PlanChangeBlocker[] = [];

  if (target.staffSeats !== null && usage.used > target.staffSeats) {
    blockers.push({
      reason: 'staff_over_allowance',
      current: usage.used,
      allowance: target.staffSeats,
      excess: usage.used - target.staffSeats,
    });
  } else if (target.staffSeats !== null && usage.used + usage.pending > target.staffSeats) {
    /**
     * The people already invited would not fit either — and a downgrade that ignored them
     * would be a way around the seat limit entirely: upgrade, send ten invitations, downgrade
     * before anyone accepts, and the shop lands on three seats with ten karigars walking in.
     * The refusal is put in front of the OWNER, who can revoke an invitation, rather than in
     * front of the karigar, who can only be turned away at the door.
     */
    blockers.push({
      reason: 'invites_over_allowance',
      current: usage.used + usage.pending,
      allowance: target.staffSeats,
      excess: usage.used + usage.pending - target.staffSeats,
    });
  }

  const direction =
    target.sortOrder === current.sortOrder
      ? 'same'
      : target.sortOrder > current.sortOrder
        ? 'upgrade'
        : 'downgrade';

  return {
    from,
    to,
    direction,
    allowed: blockers.length === 0,
    blockers,
    modulesLost: current.modules.filter((module) => !target.modules.includes(module)),
    modulesGained: target.modules.filter((module) => !current.modules.includes(module)),
  };
}

// --- The whole picture, for one screen and one endpoint -----------------------------------------

export interface Entitlements {
  readonly planKey: PlanKey;
  readonly plan: PlanDefinition;
  readonly status: SubscriptionStatus;
  /** Modules the plan includes — what the navigation shows. */
  readonly modules: readonly PlanModule[];
  /** Whether the shop may write anything at all today. */
  readonly access: AccessMode;
  readonly readOnly: boolean;
  readonly trialDaysRemaining: number | null;
  readonly graceDaysRemaining: number | null;
  readonly renewalDate: Date | null;
}

/** Everything a screen needs to know about where the shop stands, in one call. */
export function entitlementsFor(
  snapshot: SubscriptionSnapshot,
  now: Date = new Date(),
): Entitlements {
  const plan = getPlan(snapshot.planKey);
  const access = subscriptionAccess(snapshot, now);
  return {
    planKey: snapshot.planKey,
    plan,
    status: snapshot.status,
    modules: plan.modules,
    access,
    readOnly: access === 'read_only',
    trialDaysRemaining: trialDaysRemaining(snapshot, now),
    graceDaysRemaining: graceDaysRemaining(snapshot, now),
    renewalDate: renewalDate(snapshot),
  };
}

/** The snapshot as it crosses the wire, so the browser can rebuild it without guessing. */
export const subscriptionSnapshotSchema = z.object({
  planKey: planKeySchema,
  status: subscriptionStatusSchema,
  trialEndsAt: z.string().nullable(),
  currentPeriodEndsAt: z.string().nullable(),
});
export type SubscriptionSnapshotWire = z.infer<typeof subscriptionSnapshotSchema>;
